import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ORGANIZER_VIEWS } from "@/lib/organizer";
import { AssignmentEditDialog } from "@/components/AssignmentEditDialog";
import { AssignmentDeleteDialog } from "@/components/AssignmentDeleteDialog";
import { LoadError } from "@/components/PageState";
import { useOrganizer } from "./useOrganizer";
import { OrganizerTabs } from "./OrganizerTabs";
import { OrganizerToolbar } from "./OrganizerToolbar";
import { OrganizerRow } from "./OrganizerRow";
import { OrganizerBulkBar } from "./OrganizerBulkBar";
import { OrganizerPagination } from "./OrganizerPagination";
import { WeekNav } from "./WeekNav";

const EMPTY = {
  needs_grading: "Nothing is waiting for a grade. Nice work staying caught up!",
  this_week: "No assignments due this week and nothing overdue.",
  in_progress: "No drafts or returned work right now.",
  not_started: "Everything assigned has been started.",
  graded: "No graded assignments match yet.",
  archived: "You haven't archived anything yet. Use Archive on graded work to tidy your list.",
  all: "No assignments match these filters.",
};

export const AssignmentOrganizer = ({ scope, students = [], subjects = [], studentId, focusId, onCounts, onMutated }) => {
  const organizer = useOrganizer({ scope, studentId, focusId, onCounts });
  const { settings, data, loading, error, selected, allMatching } = organizer;
  const [expandedId, setExpandedId] = useState(focusId || null);
  const [gradeEditId, setGradeEditId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const reload = organizer.reload;
  const afterChange = useCallback(async () => { setGradeEditId(null); await reload(); onMutated?.(); }, [reload, onMutated]);

  const subjectTitles = useMemo(() => Object.fromEntries(subjects.map((subject) => [subject.slug, subject.title])), [subjects]);
  const items = data?.items || [];
  const pinned = data?.focus && !items.some((row) => row.id === data.focus.id) ? data.focus : null;
  const visible = pinned ? [pinned, ...items] : items;
  const editing = visible.find((row) => row.id === editingId) || null;
  const deleting = visible.find((row) => row.id === deletingId) || null;
  const pageIds = items.map((row) => row.id);
  const pageAllChecked = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const viewMeta = ORGANIZER_VIEWS.find((item) => item.key === settings.view);

  const single = useCallback(async (id, action) => {
    try {
      await api.post(`/assignments/${id}/${action}`);
      toast.success(action === "archive" ? "Archived — student work and grade are kept; restore anytime from Archived" : "Assignment restored");
      await afterChange();
    } catch (requestError) { toast.error(requestError?.response?.data?.detail || "Could not update assignment"); }
  }, [afterChange]);

  if (error) return <LoadError message={error} onRetry={organizer.reload} testId="organizer-load-error" />;

  return (
    <section className="space-y-4" data-testid={`assignment-organizer-${scope}`} aria-busy={loading}>
      <OrganizerTabs view={settings.view} counts={data?.counts} onChange={organizer.setView} />
      {viewMeta && <p className="text-xs text-slate-500" data-testid="organizer-view-hint">{viewMeta.hint}</p>}
      <OrganizerToolbar settings={settings} students={students} subjects={subjects} showStudent={!studentId && students.length > 1}
        defaultSort={data?.sort || "due_date"} defaultOrder={data?.order || "asc"} onChange={organizer.update} onReset={organizer.resetFilters} />
      {settings.view === "this_week" && <WeekNav week={data?.week} overdueCount={data?.counts?.overdue || 0} onChange={(value) => { organizer.setWeekStart(value); organizer.setPage(1); }} />}

      {items.length > 0 && (
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer px-1">
          <input type="checkbox" data-testid="organizer-select-page" checked={pageAllChecked} onChange={(event) => organizer.togglePage(pageIds, event.target.checked)} className="w-5 h-5 accent-teal-700" />
          Select all {pageIds.length} on this page
        </label>
      )}

      <div className={`space-y-2 transition-opacity ${loading ? "opacity-60" : ""}`} data-testid="organizer-list">
        {visible.map((row) => (
          <OrganizerRow key={row.id} row={row} subjectTitle={subjectTitles[row.subject] || row.subject} showStudent={!studentId}
            checked={selected.has(row.id)} expanded={expandedId === row.id} editingGrade={gradeEditId === row.id}
            onToggleSelect={organizer.toggle} onToggleExpand={() => { setGradeEditId(null); setExpandedId((current) => current === row.id ? null : row.id); }}
            onEditGrade={(id) => { setExpandedId(id); setGradeEditId(id); }}
            onEdit={setEditingId} onDelete={setDeletingId} onArchive={(id) => single(id, "archive")} onRestore={(id) => single(id, "restore")} onChanged={afterChange} />
        ))}
        {!loading && visible.length === 0 && <div className="p-6 rounded-2xl bg-slate-50 border border-dashed border-slate-200 text-sm text-slate-500 text-center" data-testid="organizer-empty">{EMPTY[settings.view]}</div>}
        {loading && !data && <div className="p-6 text-sm text-slate-400" data-testid="organizer-loading">Loading assignments…</div>}
      </div>

      {data && <OrganizerPagination page={data.page} pages={data.pages} total={data.total} pageSize={data.page_size} onPage={organizer.setPage} />}
      <OrganizerBulkBar selectedCount={selected.size} total={data?.total || 0} allMatching={allMatching} restoreMode={settings.view === "archived"}
        onSelectAllMatching={organizer.selectAllMatching} onClear={organizer.clearSelection}
        onApply={async () => { await organizer.bulk(settings.view === "archived" ? "restore" : "archive", [...selected]); onMutated?.(); }} />

      <AssignmentEditDialog key={editingId || "edit-closed"} assignment={editing} subjects={subjects} onClose={() => setEditingId(null)} onSaved={afterChange} />
      <AssignmentDeleteDialog key={deletingId || "delete-closed"} assignment={deleting} onClose={() => setDeletingId(null)} onChanged={afterChange} />
    </section>
  );
};
